import { useEffect, useRef } from 'react';
import { Button, Field, Text } from '@fluentui/react-components';
import { plainTextToHtml, sanitizeHtml, toPlainText } from '../../lib/sanitize';
import { usePaneStyles } from '../styles';

interface DescriptionEditorProps {
  html: string;
  plainMode: boolean;
  onChangeHtml: (html: string) => void;
  onTogglePlain: () => void;
  onRevertHtml: () => void;
  canRevert: boolean;
}

export function DescriptionEditor({
  html,
  plainMode,
  onChangeHtml,
  onTogglePlain,
  onRevertHtml,
  canRevert,
}: DescriptionEditorProps) {
  const styles = usePaneStyles();
  const ref = useRef<HTMLDivElement>(null);

  // Sync external html into the contenteditable when it changes from outside (load, convert).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = sanitizeHtml(html);
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [html]);

  return (
    <Field label="Description">
      <div className={styles.toolbar}>
        <Button size="small" appearance="secondary" onClick={onTogglePlain}>
          {plainMode ? 'Convert to HTML' : 'Convert to plain text'}
        </Button>
        {canRevert ? (
          <Button size="small" appearance="transparent" onClick={onRevertHtml}>
            Revert to HTML
          </Button>
        ) : null}
        <Text size={200} className={styles.count}>
          {plainMode ? 'Plain text' : 'HTML'}
        </Text>
      </div>
      <div
        ref={ref}
        className={styles.editor}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline
        aria-label="Description"
        onInput={() => {
          const el = ref.current;
          if (!el) return;
          if (plainMode) {
            onChangeHtml(plainTextToHtml(el.innerText));
          } else {
            onChangeHtml(sanitizeHtml(el.innerHTML));
          }
        }}
      />
    </Field>
  );
}

export function convertDescriptionToPlain(html: string): string {
  return plainTextToHtml(toPlainText(html));
}
